import type { TipoDeNotificacao } from './api';

/**
 * O TEXTO DE UM AVISO FORA DO PORTAL (19/10/2026) — a notificação do sistema no aplicativo do computador (etapa b) e, na
 * etapa c, o push do celular. Fora do portal o aviso fica à vista na tela de qualquer um: o título é sempre "Portal
 * Simulados", e a frase é GENÉRICA por tipo — sem título de prova, nota, nome de aluno ou turma. O detalhe fica no sino,
 * depois do login. Sem dependências: o teste roda sem a web (`foraDoPortal.test.ts`).
 */
export const TITULO_FORA_DO_PORTAL = 'Portal Simulados';

export const FRASE_FORA_DO_PORTAL: Record<TipoDeNotificacao, string> = {
  ENTREGAS: 'Um simulado seu teve novas entregas.',
  CORRECAO_PENDENTE: 'Há respostas esperando correção.',
  FECHA_SEM_ENTRAR: 'Um simulado fecha em 24 h com alunos que ainda não entraram.',
  SIMULADO_ENCERRADO: 'Um simulado seu fechou.',
  SALA_SEM_ENVIO: 'Uma sala sem internet ainda não enviou resultados.',
  PDF_PRONTO: 'Há novidade sobre o seu PDF.',
  CALIBRACAO: 'Uma calibração terminou.',
  BASE_DESATUALIZADA: 'Uma base de Rasch ficou desatualizada.',
  ROTINA_FALHOU: 'Uma rotina do servidor falhou.',
  SIMULADO_ABERTO: 'Você tem uma prova nova.',
  PERTO_DE_FECHAR: 'Uma prova sua fecha em 24 h.',
  RESULTADO_LIBERADO: 'Um resultado seu foi liberado.',
  CONFERIR_GABARITO: 'Há questões para conferir no gabarito.',
  MANUTENCAO: 'Há um alerta de manutenção do servidor.',
  ENTREGA_INCOMPLETA: 'Um simulado seu teve entregas incompletas.',
};

/** A frase para os avisos novos: um → a do tipo; mais de um → quantos. */
export function fraseParaNovos(tipos: TipoDeNotificacao[]): string | null {
  if (tipos.length === 0) return null;
  if (tipos.length === 1) return FRASE_FORA_DO_PORTAL[tipos[0]];
  return `Você tem ${tipos.length} novidades no portal.`;
}

/** "Maria Silva (2º A)" — ou só o nome, sem turma. */
export const nomeComTurma = (a: { nome: string; turma: string | null }) => (a.turma ? `${a.nome} (${a.turma})` : a.nome);

/**
 * COM O NOME (a opção de Minha conta, desligada por padrão — `nomeNoAviso.ts`): quando os avisos novos são de UM simulado e
 * um deles é o de ENTREGAS, o aviso diz quem entregou por último — "Maria Silva (2º A) entregou o #1251". (A entrega numa
 * prova com discursiva também devolve a "não lido" a correção pendente do mesmo simulado: os dois contam como um.) Nulo =
 * não há nome a dizer (avisos de mais de um simulado, nenhum de entregas, nenhum aluno visível): fica a frase genérica.
 */
type Novo = { tipo: TipoDeNotificacao; simulationId?: number | null; alunos?: Array<{ nome: string; turma: string | null; respondidas?: number; de?: number }> };
export function fraseComNome(novos: Novo[]): string | null {
  // A entrega INCOMPLETA vem primeiro (19/10/2026): é ela que pede atenção — "Maria Silva (2º A) entregou o #1251 com 5 de
  // 20 respondidas." A entrega devolve a "não lido" os dois avisos do mesmo simulado; dizer só "entregou" esconderia o porquê.
  const incompletas = novos.filter((n) => n.tipo === 'ENTREGA_INCOMPLETA');
  if (incompletas.length === 1) {
    const n = incompletas[0];
    const a = n.alunos?.[0];
    if (a && a.respondidas != null && a.de != null && n.simulationId != null && novos.every((x) => x.simulationId === n.simulationId)) {
      return `${nomeComTurma(a)} entregou o #${n.simulationId} com ${a.respondidas} de ${a.de} respondidas.`;
    }
  }
  const entregas = novos.filter((n) => n.tipo === 'ENTREGAS');
  if (entregas.length !== 1) return null;
  const n = entregas[0];
  const a = n.alunos?.[0];
  if (!a || n.simulationId == null || novos.some((x) => x.simulationId !== n.simulationId)) return null;
  return `${nomeComTurma(a)} entregou o #${n.simulationId}`;
}

/** O minuto do dia (0–1439) num fuso. */
export function minutoDoDia(agora: Date, fuso: string): number {
  const [h, m] = agora.toLocaleTimeString('en-GB', { timeZone: fuso, hour: '2-digit', minute: '2-digit', hour12: false }).split(':').map(Number);
  return (h % 24) * 60 + m;
}

/** Está no silêncio? O intervalo pode cruzar a meia-noite (22:00 às 06:00). Início igual ao fim = sem silêncio. */
export function noSilencio(agora: Date, silencio: { inicioMin: number; fimMin: number; fuso: string }): boolean {
  const { inicioMin: i, fimMin: f } = silencio;
  if (i === f) return false;
  const m = minutoDoDia(agora, silencio.fuso);
  return i < f ? m >= i && m < f : m >= i || m < f;
}
